import React from 'react';
import { Icon } from '@iconify/react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';

const CandidateDetailDrawer = ({ candidate, isOpen, onClose }) => {
  if (!candidate) return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)' }}
          />

          {/* Drawer */}
          <motion.div 
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            style={{ 
              position: 'fixed',
              top: 0,
              right: 0,
              width: '500px',
              height: '100%',
              background: 'white',
              boxShadow: '-10px 0 50px rgba(0,0,0,0.1)',
              zIndex: 2100,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Header - Fixed */}
            <div style={{ padding: '32px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '16px', background: 'var(--primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 800 }}>
                  {candidate.full_name?.charAt(0)}
                </div>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)' }}>Candidate Details</h2>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>{candidate.unique_id}</p>
                </div>
              </div>
              <button onClick={onClose} style={{ background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Icon icon="ion:close-outline" width="24" />
              </button>
            </div>

            {/* Scrollable Body */}
            <div className="hide-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '32px 40px' }}>
              <div style={{ marginBottom: '32px' }}>
                <h3 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '8px' }}>{candidate.full_name}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', background: 'rgba(128,0,0,0.05)', color: 'var(--primary)', borderRadius: '20px', border: '1px solid var(--border-light)' }}>
                    CURRENT: {candidate.current_stage}
                  </span>
                  <span style={{ fontSize: '11px', fontWeight: 700, padding: '4px 10px', background: '#f0f9ff', color: '#0369a1', borderRadius: '20px', border: '1px solid #bae6fd' }}>
                    CHEST: {candidate.chest_number || 'PENDING'}
                  </span>
                </div>
              </div>

              {/* Graphical Journey Graph */}
              <div style={{ marginBottom: '40px', padding: '24px', background: '#f8fafc', borderRadius: '20px', border: '1px solid #e2e8f0' }}>
                 <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Mission Pipeline Flow</h4>
                 <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 8px' }}>
                   <GraphNode icon="solar:login-bold-duotone" active={true} label="ENTRY" />
                   <GraphLine active={['BATCHING', 'TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                   <GraphNode icon="solar:users-group-rounded-bold-duotone" active={['BATCHING', 'TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="BATCH" />
                   <GraphLine active={['TRACKER', 'PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                   <GraphNode icon="solar:clipboard-check-bold-duotone" active={['PHYSICAL_TEST', 'MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="PHYSICAL" />
                   <GraphLine active={['MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} />
                   <GraphNode icon="solar:medical-kit-bold-duotone" active={['MEDICAL', 'COMPLETED'].includes(candidate.current_stage)} label="MED" />
                 </div>
              </div>

              {/* Activity Timeline */}
              <div style={{ marginBottom: '40px' }}>
                <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Historical Activity Logs</h4>
                {(!candidate.activity_log || candidate.activity_log.length === 0) ? (
                  <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px', fontSize: '14px', border: '2px dashed #f1f5f9', borderRadius: '20px' }}>
                    No mission logs recorded yet.
                  </div>
                ) : (
                   <div style={{ paddingLeft: '8px' }}>
                    {[...candidate.activity_log].sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)).map((log, i) => {
                      const isNegative = ['REJECTED', 'FAIL', 'INJURY', 'INJURED', 'DISQUALIFIED', 'VOLUNTARY_DROP', 'INCIDENT_REPORTED', 'DISCONTINUED'].includes(log.action) || log.details?.includes('Result: FAIL');
                      return (
                        <div key={i} style={{ display: 'flex', gap: '20px', marginBottom: '32px', position: 'relative' }}>
                          <div style={{ 
                            width: '28px', height: '28px', borderRadius: '50%', 
                            background: isNegative ? '#fee2e2' : '#f0fdf4', 
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1,
                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                          }}>
                            <Icon icon={isNegative ? "ion:close-circle" : "ion:checkmark-circle"} width="16" color={isNegative ? '#ef4444' : '#10b981'} />
                          </div>
                        {i < candidate.activity_log.length - 1 && <div style={{ position: 'absolute', left: '13px', top: '28px', bottom: '-32px', width: '2px', background: '#f1f5f9' }}></div>}
                        <div>
                          <div style={{ fontSize: '14px', fontWeight: 800, color: 'var(--text-dark)' }}>{log.details}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', fontWeight: 600 }}>{new Date(log.timestamp).toLocaleString()} • {log.action}</div>
                        </div>
                      </div>
                      );
                    })}
                   </div>
                )}
              </div>

              {/* Clearance Status Section */}
              <div>
                <h4 style={{ fontSize: '12px', fontWeight: 800, color: 'var(--text-muted)', marginBottom: '20px', textTransform: 'uppercase', letterSpacing: '1px' }}>Test Clearance Data</h4>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {(candidate.test_results || []).length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>No test records available.</div>
                  ) : (
                    candidate.test_results.map((test, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', background: test.result === 'PASS' ? '#f0fdf4' : '#fdf2f2', borderRadius: '16px', border: `1px solid ${test.result === 'PASS' ? '#bbf7d0' : '#fecaca'}` }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--text-dark)' }}>{test.stage_name} Result</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 600 }}>VERIFIED ON SYSTEM</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                           <span style={{ fontSize: '12px', fontWeight: 900, color: test.result === 'PASS' ? '#166534' : '#991b1b' }}>{test.result}</span>
                           <Icon icon={test.result === 'PASS' ? "ion:checkmark-circle" : "ion:close-circle"} width="18" color={test.result === 'PASS' ? '#166534' : '#991b1b'} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '24px 40px', borderTop: '1px solid var(--border-light)', background: '#f8fafc' }}>
              <button disabled className="btn-secondary" style={{ width: '100%', opacity: 0.7, cursor: 'not-allowed' }}>
                Operational Profile Locked
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};

// Internal helpers from Analytics.jsx
const GraphNode = ({ icon, active, label }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 2 }}>
    <div style={{ 
      width: '36px', 
      height: '36px', 
      borderRadius: '12px', 
      background: active ? 'var(--primary)' : '#cbd5e1', 
      color: 'white', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      marginBottom: '8px',
      boxShadow: active ? '0 8px 16px rgba(128,0,0,0.15)' : 'none',
      transition: 'all 0.3s'
    }}>
      <Icon icon={icon} width="18" />
    </div>
    <div style={{ fontSize: '10px', fontWeight: 800, color: active ? 'var(--primary)' : 'var(--text-muted)' }}>{label}</div>
  </div>
);

const GraphLine = ({ active }) => (
  <div style={{ flex: 1, height: '3px', background: active ? 'var(--primary)' : '#e2e8f0', margin: '0 -4px', position: 'relative', marginTop: '-18px', zIndex: 1 }}>
    {active && (
      <div style={{ 
        position: 'absolute', 
        top: 0, left: 0, height: '100%', width: '100%', 
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)', 
        animation: 'shimmer 1.5s infinite linear' 
      }}></div>
    )}
  </div>
);

export default CandidateDetailDrawer;
